import Phaser from 'phaser';
import { disableMenuScroll } from '../utils/menu-scroll.js';
import { sfx } from '../audio/SFX.js';
import { getCachedHats, refreshHatInventory } from '../auth/hat-inventory.js';
import { playContext as playMusic } from '../audio/Music.js';

/** Color = perk role, shown for reference only */
const COLOR_OPTIONS = [
  { name: 'Economy',   label: 'ECONOMY',   head: 0xFFDD00, body: 0xCC9900 },
  { name: 'PvP',       label: 'PVP',       head: 0xFF4444, body: 0xCC1111 },
  { name: 'Speed',     label: 'MOVEMENT',  head: 0x44DDFF, body: 0x0099DD },
  { name: 'High Risk', label: 'HIGH RISK', head: 0xFF55FF, body: 0xCC22CC },
];

/** Available hats — matches BootScene load order */
const HAT_OPTIONS = [
  { id: 'none',   name: 'None',    key: '' },
  { id: 'wizard', name: 'Wizard',  key: 'hat_wizard' },
  { id: 'crown',  name: 'Crown',   key: 'hat_crown' },
  { id: 'cowboy', name: 'Cowboy',  key: 'hat_cowboy' },
  { id: 'viking', name: 'Viking',  key: 'hat_viking' },
  { id: 'party',  name: 'Party',   key: 'hat_party' },
  { id: 'cap',    name: 'Cap',     key: 'hat_cap' },
  { id: 'knight', name: 'Knight',  key: 'hat_knight' },
  { id: 'pepper', name: 'Pepper',  key: 'hat_pepper' },
];

/** Hats every player owns by default — the rest unlock from chest drops. */
const DEFAULT_OWNED_HATS = new Set(['none', 'cap']);

/** Snake customization screen — Stardew Valley style with live preview */
export class SkinSelectScene extends Phaser.Scene {
  private colorIndex: number = 0;
  private hatIndex: number = 0;
  /** Hat IDs the player currently has access to. Hydrated from the
   *  server on enter for logged-in wallets; demo / not-logged-in users
   *  see only the defaults. */
  private ownedHats: Set<string> = new Set(DEFAULT_OWNED_HATS);
  private lockedThumbs: Map<number, () => void> = new Map();

  // Preview snake pieces
  private previewHead!: Phaser.GameObjects.Image;
  private previewBody: Phaser.GameObjects.Image[] = [];
  private previewHat!: Phaser.GameObjects.Image;
  private previewContainer!: Phaser.GameObjects.Container;

  // Labels
  private colorLabel!: Phaser.GameObjects.Text;
  private hatLabel!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'SkinSelectScene' });
  }

  preload(): void {
    if (!this.textures.exists('skins_bg')) {
      this.load.image('skins_bg', 'assets/sprites/menus/skinsbackground.png');
    }
    if (!this.textures.exists('snake_head')) {
      this.load.image('snake_head', 'assets/sprites/snake/snake_head_grey.png');
    }
    if (!this.textures.exists('snake_body')) {
      this.load.image('snake_body', 'assets/sprites/snake/snake_body_grey.png');
    }
    const hatNames = ['wizard', 'crown', 'cowboy', 'viking', 'party', 'cap', 'knight', 'pepper'];
    for (let i = 0; i < hatNames.length; i++) {
      const key = `hat_${hatNames[i]}`;
      if (this.textures.exists(key)) continue;
      this.load.image(key, `assets/sprites/hats/frame_${String(i + 1).padStart(3, '0')}.png`);
    }
  }

  create(): void {
    disableMenuScroll();
    playMusic(this.game, 'menu');
    this.cameras.main.setZoom(1);
    this.cameras.main.setScroll(0, 0);
    this.cameras.main.fadeIn(300);
    this.previewBody = [];
    // Seed from the shared cache (warm from login). Defaults are baked in.
    this.ownedHats = new Set(getCachedHats());
    for (const id of DEFAULT_OWNED_HATS) this.ownedHats.add(id);
    this.lockedThumbs.clear();

    // Load saved preferences
    this.loadPreferences();
    // Refresh from server in the background — catches hats unlocked
    // since login (e.g. just earned in the last match).
    void this.refreshFromServer();

    const { width, height } = this.scale;
    const cx = width / 2;

    // === Background: wardrobe image scaled to cover the canvas ===
    if (this.textures.exists('skins_bg')) {
      this.add.image(cx, height / 2, 'skins_bg')
        .setDisplaySize(width, height)
        .setDepth(0);
    } else {
      // Fallback if asset failed to load
      this.add.rectangle(cx, height / 2, width, height, 0x030308).setDepth(0);
    }

    // === Dark overlay over the wardrobe interior ===
    // Sized as a focused panel just larger than the UI inside, so the
    // wardrobe's chips/sword/gems decoration still shows around it
    // (gives a "card sitting in the cabinet" feel instead of blacking
    // out the whole opening).
    const intLeft   = width  * 0.29;
    const intRight  = width  * 0.71;
    const intTop    = height * 0.13;
    const intBottom = height * 0.70;
    const intCx = (intLeft + intRight) / 2;
    const intCy = (intTop + intBottom) / 2;
    const intW  = intRight - intLeft;
    const intH  = intBottom - intTop;
    this.add.rectangle(intCx, intCy, intW, intH, 0x000000, 0.94).setDepth(2);
    // Soft inner edge so the black panel blends with the wood frame
    this.add.rectangle(intCx, intCy, intW, intH, 0x000000, 0)
      .setStrokeStyle(2, 0x1a0e08, 0.6).setDepth(3);

    // Subtle dust particles inside the wardrobe (atmospheric)
    if (!this.textures.exists('skin_dust')) {
      const g = this.add.graphics();
      g.fillStyle(0xffffff, 1);
      g.fillRect(0, 0, 2, 2);
      g.generateTexture('skin_dust', 2, 2);
      g.destroy();
    }
    this.add.particles(intCx, intCy, 'skin_dust', {
      x: { min: -intW / 2, max: intW / 2 },
      y: { min: -intH / 2, max: intH / 2 },
      scale: { min: 0.2, max: 0.5 },
      alpha: { start: 0.15, end: 0 },
      lifespan: { min: 4000, max: 8000 },
      speed: { min: 3, max: 6 },
      angle: { min: 260, max: 280 },
      frequency: 250,
      quantity: 1,
      tint: [0xffd700, 0xffaa44],
      blendMode: 'ADD',
    }).setDepth(4);

    // Title — sits in the dark space above the wardrobe interior
    this.add.text(cx, height * 0.04, 'CUSTOMIZE YOUR SNAKE', {
      fontFamily: 'monospace', fontSize: '24px', color: '#ffd700',
      stroke: '#000', strokeThickness: 5, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(10);

    // ============ PREVIEW FRAME (left half of wardrobe interior) ============
    const frameX = intCx - intW * 0.22;
    const frameY = intCy - intH * 0.05;
    const frameW = Math.min(180, intW * 0.42);
    const frameH = frameW * 1.2;

    // Subtle frame outline so preview has its own card inside the wardrobe
    this.add.rectangle(frameX, frameY, frameW, frameH, 0x000000, 0)
      .setStrokeStyle(1, 0x9d00ff, 0.5).setDepth(5);

    // Frame label
    this.add.text(frameX, frameY - frameH / 2 - 12, 'PREVIEW', {
      fontFamily: 'monospace', fontSize: '10px', color: '#888899',
    }).setOrigin(0.5).setDepth(10);

    // Build preview snake inside a container (clipped to frame)
    this.buildPreview(frameX, frameY, frameW, frameH);

    // ============ CONTROLS (right half of wardrobe interior) ============
    const ctrlX = intCx + intW * 0.22;
    let ctrlY = intCy - intH * 0.30;
    const rowGap = 70;

    // --- COLOR (role-based, display only for preview) ---
    this.makeSelector(ctrlX, ctrlY, 'ROLE COLOR', this.colorIndex, COLOR_OPTIONS.length,
      () => this.getColorName(),
      (dir) => {
        this.colorIndex = (this.colorIndex + dir + COLOR_OPTIONS.length) % COLOR_OPTIONS.length;
        this.updatePreview();
      },
      (label) => { this.colorLabel = label; }
    );

    // Role color swatches
    const swatchY = ctrlY + 32;
    COLOR_OPTIONS.forEach((c, i) => {
      const dotX = ctrlX + (i - (COLOR_OPTIONS.length - 1) / 2) * 28;
      const dot = this.add.circle(dotX, swatchY, 8, c.head).setDepth(10)
        .setStrokeStyle(1, 0x333344)
        .setInteractive({ useHandCursor: true });
      dot.on('pointerdown', () => {
        sfx(this, 'button_click');
        this.colorIndex = i;
        this.updatePreview();
      });
    });

    // Small note: color is determined by perk
    this.add.text(ctrlX, swatchY + 18, 'Color is set by your perk choice', {
      fontFamily: 'monospace', fontSize: '8px', color: '#444455',
    }).setOrigin(0.5).setDepth(10);

    ctrlY += rowGap + 20;

    // --- HAT ---
    this.makeSelector(ctrlX, ctrlY, 'HAT', this.hatIndex, HAT_OPTIONS.length,
      () => this.getHatName(),
      (dir) => {
        // Skip locked hats so the arrow nav only walks unlocked ones
        let next = this.hatIndex;
        for (let i = 0; i < HAT_OPTIONS.length; i++) {
          next = (next + dir + HAT_OPTIONS.length) % HAT_OPTIONS.length;
          if (this.ownedHats.has(HAT_OPTIONS[next].id)) {
            this.hatIndex = next;
            this.updatePreview();
            return;
          }
        }
      },
      (label) => { this.hatLabel = label; }
    );

    // Hat thumbnail row — fits within the right half of the wardrobe interior
    const thumbY = ctrlY + 36;
    const thumbsPerRow = Math.min(HAT_OPTIONS.length, 9);
    const maxRowWidth = intW * 0.44;
    const thumbSpacing = Math.min(34, maxRowWidth / thumbsPerRow);
    const thumbSize = Math.max(20, thumbSpacing - 4);
    const thumbStartX = ctrlX - ((thumbsPerRow - 1) * thumbSpacing) / 2;

    HAT_OPTIONS.forEach((h, i) => {
      const tx = thumbStartX + i * thumbSpacing;
      const owned = this.ownedHats.has(h.id);
      const bg = this.add.rectangle(tx, thumbY, thumbSize, thumbSize, 0x1a1a2e)
        .setStrokeStyle(1, owned ? 0x333344 : 0x222230).setDepth(10)
        .setInteractive({ useHandCursor: true });

      let img: Phaser.GameObjects.Image | null = null;
      if (h.key && this.textures.exists(h.key)) {
        img = this.add.image(tx, thumbY, h.key)
          .setDisplaySize(thumbSize - 4, thumbSize - 4).setDepth(11);
        if (!owned) {
          img.setTint(0x404040);
          img.setAlpha(0.55);
        }
      } else {
        this.add.text(tx, thumbY, '—', {
          fontFamily: 'monospace', fontSize: '14px', color: '#555566',
        }).setOrigin(0.5).setDepth(11);
      }

      // Lock badge for unowned hats — small padlock at the corner.
      // Keep a reference so the refresh callback can remove it once the
      // server confirms the hat is unlocked (otherwise the padlock stays
      // on top of an already-selectable hat).
      let lockBadge: Phaser.GameObjects.Text | null = null;
      if (!owned) {
        lockBadge = this.add.text(tx + thumbSize / 2 - 4, thumbY - thumbSize / 2 + 4, '🔒', {
          fontFamily: 'monospace', fontSize: '10px',
        }).setOrigin(1, 0).setDepth(12);
      }

      // Save a refresh callback so fetchOwnedHats can re-skin this thumb
      // once the inventory comes back from the server.
      this.lockedThumbs.set(i, () => {
        const nowOwned = this.ownedHats.has(h.id);
        bg.setStrokeStyle(1, nowOwned ? 0x333344 : 0x222230);
        if (img) {
          if (nowOwned) {
            img.clearTint();
            img.setAlpha(1);
          } else {
            img.setTint(0x404040);
            img.setAlpha(0.55);
          }
        }
        if (nowOwned && lockBadge) {
          lockBadge.destroy();
          lockBadge = null;
        }
      });

      bg.on('pointerdown', () => {
        sfx(this, 'button_click');
        if (!this.ownedHats.has(h.id)) {
          this.flashLockedHint(tx, thumbY - thumbSize / 2 - 6);
          return;
        }
        this.hatIndex = i;
        this.updatePreview();
      });
    });

    // ============ BOTTOM BUTTONS (sit on the felt table area) ============
    const confirmBtn = this.add.text(cx, height * 0.86, '[ CONFIRM ]', {
      fontFamily: 'monospace', fontSize: '24px', color: '#00ff88',
      stroke: '#000', strokeThickness: 6, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(10).setInteractive({ useHandCursor: true });

    confirmBtn.on('pointerover', () => { sfx(this, 'button_hover'); confirmBtn.setColor('#ffffff'); });
    confirmBtn.on('pointerout', () => confirmBtn.setColor('#00ff88'));
    confirmBtn.on('pointerdown', () => {
      sfx(this, 'perk_confirmed');
      this.savePreferences();
      this.cameras.main.fadeOut(300, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.start('MainMenuScene');
      });
    });

    const backBtn = this.add.text(cx, height * 0.94, 'Back to Menu', {
      fontFamily: 'monospace', fontSize: '13px', color: '#cccccc',
      stroke: '#000', strokeThickness: 3,
    }).setOrigin(0.5).setDepth(10).setInteractive({ useHandCursor: true });

    backBtn.on('pointerover', () => { sfx(this, 'button_hover'); backBtn.setColor('#ffffff'); });
    backBtn.on('pointerout', () => backBtn.setColor('#cccccc'));
    backBtn.on('pointerdown', () => {
      sfx(this, 'button_click');
      this.cameras.main.fadeOut(300, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.start('MainMenuScene');
      });
    });

    // Initial render
    this.updatePreview();
  }

  /** Arrow selector row: ◀ LABEL ▶ */
  private makeSelector(
    x: number, y: number, title: string,
    _initial: number, _total: number,
    getName: () => string,
    onChange: (dir: number) => void,
    setLabel: (label: Phaser.GameObjects.Text) => void,
  ): void {
    this.add.text(x, y - 18, title, {
      fontFamily: 'monospace', fontSize: '11px', color: '#666677',
    }).setOrigin(0.5).setDepth(10);

    const label = this.add.text(x, y + 4, getName(), {
      fontFamily: 'monospace', fontSize: '16px', color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(10);
    setLabel(label);

    const leftArrow = this.add.text(x - 80, y + 4, '◀', {
      fontFamily: 'monospace', fontSize: '22px', color: '#9d00ff',
    }).setOrigin(0.5).setDepth(10).setInteractive({ useHandCursor: true });

    leftArrow.on('pointerover', () => leftArrow.setColor('#ffffff'));
    leftArrow.on('pointerout', () => leftArrow.setColor('#9d00ff'));
    leftArrow.on('pointerdown', () => {
      sfx(this, 'card_deal');
      onChange(-1);
      label.setText(getName());
      this.tweens.add({ targets: label, scaleX: 1.2, scaleY: 1.2, duration: 80, yoyo: true });
    });

    const rightArrow = this.add.text(x + 80, y + 4, '▶', {
      fontFamily: 'monospace', fontSize: '22px', color: '#9d00ff',
    }).setOrigin(0.5).setDepth(10).setInteractive({ useHandCursor: true });

    rightArrow.on('pointerover', () => rightArrow.setColor('#ffffff'));
    rightArrow.on('pointerout', () => rightArrow.setColor('#9d00ff'));
    rightArrow.on('pointerdown', () => {
      sfx(this, 'card_deal');
      onChange(1);
      label.setText(getName());
      this.tweens.add({ targets: label, scaleX: 1.2, scaleY: 1.2, duration: 80, yoyo: true });
    });
  }

  /** Build the snake preview — centered in the frame, properly scaled */
  private buildPreview(frameX: number, frameY: number, frameW: number, frameH: number): void {
    // Snake segments in a nice downward S-curve (grid coords)
    const path = [
      { x: 0, y: 0 },   // head (top)
      { x: 0, y: 1 },   // body
      { x: 0, y: 2 },   // body
      { x: 1, y: 2 },   // body (turn right)
      { x: 1, y: 3 },   // body (tail)
    ];

    // Scale to fit inside frame with padding
    const cellSize = 32;
    // Find bounding box of path
    const minX = Math.min(...path.map(p => p.x));
    const maxX = Math.max(...path.map(p => p.x));
    const minY = Math.min(...path.map(p => p.y));
    const maxY = Math.max(...path.map(p => p.y));
    const pathW = (maxX - minX + 1) * cellSize;
    const pathH = (maxY - minY + 1) * cellSize;

    // Scale so snake fits in ~70% of frame
    const fitScale = Math.min(
      (frameW * 0.65) / pathW,
      (frameH * 0.65) / pathH
    );

    const scaledCell = cellSize * fitScale;

    // Center the path in the frame
    const pathCenterX = (minX + maxX + 1) / 2 * scaledCell;
    const pathCenterY = (minY + maxY + 1) / 2 * scaledCell;
    const offsetX = frameX - pathCenterX;
    const offsetY = frameY - pathCenterY;

    // Body segments (rendered back to front)
    for (let i = path.length - 1; i >= 1; i--) {
      const seg = path[i];
      const body = this.add.image(
        offsetX + (seg.x + 0.5) * scaledCell,
        offsetY + (seg.y + 0.5) * scaledCell,
        'snake_body'
      ).setScale(fitScale).setDepth(20 + i);
      this.previewBody.push(body);
    }

    // Head — facing UP (angle 270 = up)
    const head = path[0];
    const headX = offsetX + (head.x + 0.5) * scaledCell;
    const headY = offsetY + (head.y + 0.5) * scaledCell;

    this.previewHead = this.add.image(headX, headY, 'snake_head')
      .setScale(fitScale).setDepth(30).setAngle(270);

    // Hat — slightly behind head center (toward body)
    this.previewHat = this.add.image(headX, headY + scaledCell * 0.35, 'snake_head')
      .setScale(fitScale * 0.32, fitScale * -0.32) // negative Y = flip vertically
      .setDepth(31).setVisible(false);
  }

  private updatePreview(): void {
    const color = COLOR_OPTIONS[this.colorIndex];
    const hat = HAT_OPTIONS[this.hatIndex];

    // Update colors
    this.previewHead.setTint(color.head);
    for (const body of this.previewBody) {
      body.setTint(color.body);
    }

    // Update hat
    if (hat.key && this.textures.exists(hat.key)) {
      this.previewHat.setTexture(hat.key);
      this.previewHat.setVisible(true);
    } else {
      this.previewHat.setVisible(false);
    }

    // Update labels
    if (this.colorLabel) this.colorLabel.setText(color.name.toUpperCase());
    if (this.hatLabel) this.hatLabel.setText(hat.name.toUpperCase());

    // Subtle preview shake
    this.tweens.add({
      targets: this.previewHead,
      scaleX: this.previewHead.scaleX * 1.08,
      scaleY: this.previewHead.scaleY * 1.08,
      duration: 80,
      yoyo: true,
    });
  }

  private getColorName(): string {
    return COLOR_OPTIONS[this.colorIndex].name.toUpperCase();
  }

  private getHatName(): string {
    return HAT_OPTIONS[this.hatIndex].name.toUpperCase();
  }

  private savePreferences(): void {
    const prefs = {
      hatId: HAT_OPTIONS[this.hatIndex].id,
    };
    localStorage.setItem('snake_skin', JSON.stringify(prefs));
    this.registry.set('snakeHat', HAT_OPTIONS[this.hatIndex]);
  }

  private loadPreferences(): void {
    try {
      const saved = localStorage.getItem('snake_skin');
      if (saved) {
        const prefs = JSON.parse(saved);
        const hatIdx = HAT_OPTIONS.findIndex(h => h.id === prefs.hatId);
        this.hatIndex = hatIdx >= 0 ? hatIdx : 0;
      }
    } catch {
      // ignore
    }
  }

  /** Pull the latest hat inventory from the server (shared cache).
   *  No-op for demo / not-logged-in (defaults stay). */
  private async refreshFromServer(): Promise<void> {
    const fresh = await refreshHatInventory();
    for (const id of fresh) this.ownedHats.add(id);
    for (const refresh of this.lockedThumbs.values()) refresh();
    // If the saved hat is now locked (defensive — shouldn't happen)
    // drop back to 'none'.
    const currentHat = HAT_OPTIONS[this.hatIndex];
    if (currentHat && !this.ownedHats.has(currentHat.id)) {
      this.hatIndex = 0;
      this.updatePreview();
    }
  }

  /** Tiny "Earn from chests" toast that fades over the locked thumb. */
  private flashLockedHint(x: number, y: number): void {
    const t = this.add.text(x, y, 'EARN FROM CHESTS', {
      fontFamily: 'monospace', fontSize: '9px', color: '#ffd166',
      stroke: '#000', strokeThickness: 2, fontStyle: 'bold',
    }).setOrigin(0.5, 1).setDepth(50).setAlpha(0);
    this.tweens.add({
      targets: t, alpha: 1, y: y - 6, duration: 140, ease: 'Quad.easeOut',
      yoyo: true, hold: 700, onComplete: () => t.destroy(),
    });
  }
}
